"use client";

import { cx } from "class-variance-authority";
import {
	type ComponentProps,
	type PropsWithChildren,
	type ReactNode,
	useRef,
} from "react";
import {
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	type PopoverProps,
	type Selection,
} from "react-aria-components";

interface Props
	extends PropsWithChildren<{ button: ReactNode; className?: string }>,
		Pick<PopoverProps, "placement"> {
	children: ReactNode;
	button: ReactNode;
	className?: string;
	selectedKeys?: Selection;
}

export function Dropdown({
	children,
	button,
	className,
	placement,
	selectedKeys,
}: Props) {
	const triggerRef = useRef(null);

	return (
		<MenuTrigger>
			<div className={className} ref={triggerRef}>
				{button}
			</div>
			<Popover
				triggerRef={triggerRef}
				placement={placement}
				className={cx(
					"transition-opacity",
					"data-[exiting]:opacity-0 data-[entering]:opacity-0",
				)}
			>
				<Menu
					selectionMode="single"
					selectedKeys={selectedKeys}
					className="bg-background inset-shadow-glow/20 border border-glow py-3.5 flex flex-col rounded-xl"
				>
					{children}
				</Menu>
			</Popover>
		</MenuTrigger>
	);
}

export function Item({ className, ...props }: ComponentProps<typeof MenuItem>) {
	return (
		<MenuItem
			{...props}
			className={cx(
				"flex items-center pl-4 pr-6 py-1 gap-1.5 cursor-pointer animate hover:bg-background-muted",
				className,
			)}
		/>
	);
}
